import {useEffect, useState, useCallback} from "react";
import { format } from 'date-fns';
import {useNavigate} from "react-router-dom";
import {ParseAPI, SERVER_URL} from "../API";
import Parse, { ParseClasses } from "../api/config";
import { useAuth } from "./AuthContext";

export const MyRequests = ({setCurrentTab}) => {
    const { user } = useAuth();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Generating PDF...');

    const navigate = useNavigate();

    const fetchForms = useCallback(async () => {
        // Get current user email from auth context
        const userEmail = user ? user.email : null;

        if (!userEmail) {
            console.error('No authenticated user found');
            return;
        }

        try {

            // Try to fetch forms with Parse
            const formQuery = new Parse.Query(ParseClasses.Form);
            formQuery.equalTo('owner', userEmail);
            formQuery.descending('createdAt');

            const parseResults = await formQuery.find();

            if (parseResults && parseResults.length > 0) {
                // Map Parse objects with enhanced status information
                const parseForms = await Promise.all(parseResults.map(async (form) => {
                    let status = 'Submitted';
                    let denialInfo = null;

                    if (form.get('isDraft')) {
                        status = 'Draft';
                    } else if (form.get('isCancelled')) {
                        status = 'Cancelled';
                    } else if (form.get('isDenied')) {
                        status = 'Denied';
                        // Get denial information
                        try {
                            const approvalStatus = await Parse.Cloud.run('getFormApprovalStatus', { formId: form.id });
                            if (approvalStatus.success && approvalStatus.denialInfo) {
                                denialInfo = approvalStatus.denialInfo;
                            }
                        } catch (e) {
                            console.warn('Could not fetch denial info for form:', form.id);
                        }
                    } else if (form.get('isFullyApproved')) {
                        status = 'Fully Approved';
                    } else {
                        // Check if partially approved
                        const completedApprovals = form.get('completedApprovals') || [];
                        if (completedApprovals.length > 0) {
                            status = 'Partially Approved';
                        }
                    }

                    return {
                        id: form.id,
                        title: form.get('eventName') || 'Untitled Event',
                        createdAt: form.get('createdAt'),
                        status: status,
                        type: form.get('type'),
                        formData: form.toJSON(),
                        denialInfo: denialInfo,
                        isDenied: form.get('isDenied') || false,
                        isFullyApproved: form.get('isFullyApproved') || false,
                        isCancelled: form.get('isCancelled') || false,
                        cancelledAt: form.get('cancelledAt'),
                        cancellationReason: form.get('cancellationReason')
                    };
                }));

                setForms(parseForms);
            } else {
                // Fallback to original API if no results from Parse
                if (userEmail) {
                    const response = await fetch(SERVER_URL + '/forms/' + userEmail);
                    const data = await response.json();
                    setForms(data);
                }
            }
        } catch (error) {
            console.error('Error fetching forms with Parse:', error);

            // Fallback to original API
            try {
                if (userEmail) {
                    const response = await fetch(SERVER_URL + '/forms/' + userEmail);
                    const data = await response.json();
                    setForms(data);
                }
            } catch (fallbackError) {
                console.error('Fallback fetch also failed:', fallbackError);
            }
        }
    }, [user]);

    useEffect(() => {
        // Fetch data when user is available
        if (user) {
            fetchForms();
        }
    }, [user, fetchForms]);

    const deleteForm = async (id) => {
        try {
            // Try to delete form with Parse
            const formQuery = new Parse.Query(ParseClasses.Form);
            const formObject = await formQuery.get(id);
            
            if (formObject) {
                // Before deleting the main form, check and delete any linked subforms
                const subformFields = [
                    'campusLockUpForm', 
                    'equipmentRequestForm', 
                    'platformChangeRequestForm', 
                    'liveServicesTechRequestForm', 
                    'performingArtsTechRequestForm'
                ];
                
                // Delete any linked subforms
                for (const field of subformFields) {
                    const subform = formObject.get(field);
                    if (subform) {
                        await subform.destroy();
                    }
                }
                
                // Delete the main form
                await formObject.destroy();
                
                // Refresh the form list
                fetchForms();
            } else {
                throw new Error('Form not found in Parse');
            }
        } catch (error) {
            console.error('Error deleting form with Parse:', error);
            
            // Fallback to original API
            try {
                await fetch(`${SERVER_URL}/forms/${id}`, {
                    method: 'DELETE'
                });
                fetchForms();
            } catch (fallbackError) {
                console.error('Fallback delete also failed:', fallbackError);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return format(date, 'MMMM dd, yyyy');
    };

    const editForm = (id) => {
        // triggerNavigationChange()
        navigate(`/home/form/${id}`)
    }

    const generatePdf = async (formId) => {
        try {
            console.log("Generating PDF for form with id: " + formId);
            
            // Show loading state - could add a spinner here
            setLoading(true);
            
            // Try to use Parse Cloud function for PDF generation
            try {
                // Call Parse Cloud function to generate PDF
                const result = await Parse.Cloud.run('generatePDF', { formId });
                
                if (result && result.url) {
                    // Use the PDF URL returned from the cloud function
                    const link = document.createElement('a');
                    link.href = result.url;
                    link.setAttribute('download', `form-${formId}.pdf`);
                    link.setAttribute('target', '_blank');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    setLoading(false);
                    return;
                }
                
                throw new Error('No PDF URL returned from cloud function');
            } catch (parseError) {
                console.error("Error using Parse Cloud for PDF generation: ", parseError);
                // Fall back to the original method
            }
            
            // Fallback to original API if Parse Cloud function fails
            const response = await fetch(SERVER_URL + '/generate-pdf/' + formId, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/pdf'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to generate PDF: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();

            // Create a URL for the blob and set it as the href attribute of an anchor element
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `form-${formId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            console.error("Error Generating PDF: ", e);
            alert("Error generating PDF. Please try again later.");
        } finally {
            setLoading(false);
        }
    }

    const duplicateForm = async (id) => {
        try {
            setLoading(true);
            
            // Fetch the form data using Parse
            const formQuery = new Parse.Query(ParseClasses.Form);
            const formObject = await formQuery.get(id);
            
            if (formObject) {
                // Get form data
                const formData = formObject.toJSON();
                
                // Create a modified copy of the form data
                const duplicatedData = {
                    ...formData,
                    // Remove date and time fields
                    dayDateOfEvent: '',
                    startTime: '',
                    endTime: '',
                    prepTime: '',
                    // Add a "copy" suffix to the event name
                    eventName: `${formData.eventName} (Copy)`
                };
                
                // Store the duplicated data in localStorage for the form to use
                localStorage.setItem('duplicatedFormData', JSON.stringify(duplicatedData));
                
                // Navigate to the form page
                navigate('/home/form');
            } else {
                throw new Error('Form not found in Parse');
            }
        } catch (parseError) {
            console.error('Error duplicating form with Parse:', parseError);
            
            // Fallback to original API
            try {
                const response = await fetch(`${SERVER_URL}/forms/id/${id}`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch form: ${response.status} ${response.statusText}`);
                }
                
                const formData = await response.json();
                
                // Create a modified copy of the form data
                const duplicatedData = {
                    ...formData,
                    // Remove date and time fields
                    dayDateOfEvent: '',
                    startTime: '',
                    endTime: '',
                    prepTime: '',
                    // Add a "copy" suffix to the event name
                    eventName: `${formData.eventName} (Copy)`
                };
                
                // Store the duplicated data in localStorage for the form to use
                localStorage.setItem('duplicatedFormData', JSON.stringify(duplicatedData));
                
                // Navigate to the form page
                navigate('/home/form');
            } catch (fallbackError) {
                console.error('Fallback fetch also failed:', fallbackError);
                alert('Error duplicating form. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    }

    // Handle converting Save the Date to full Master Calendar request
    const convertToFullRequest = async (id) => {
        try {
            setLoading(true);

            // Call the API method to convert the form
            const masterCalendarForm = await ParseAPI.convertSaveTheDateToMasterCalendar(id);

            // Navigate to the new form to edit it
            navigate(`/home/form/${masterCalendarForm.id}`);
        } catch (error) {
            console.error('Error converting Save the Date to full request:', error);
            alert('Error converting to full request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle sending approval emails for a form
    const sendApprovalEmails = async (formId) => {
        try {
            setLoading(true);
            setLoadingMessage('Sending approval emails...');

            const result = await Parse.Cloud.run('sendApprovalEmails', { formId });

            if (result.success) {
                alert(`✅ ${result.message}\n\nEmails sent: ${result.emailsSent}\nPending roles: ${result.pendingRoles.join(', ')}`);
            } else {
                alert(`❌ ${result.message}`);
            }
        } catch (error) {
            console.error('Error sending approval emails:', error);
            alert('❌ Error sending approval emails. Please try again later.');
        } finally {
            setLoading(false);
            setLoadingMessage('Generating PDF...');
        }
    };

    // Handle resubmitting a denied form
    const resubmitDeniedForm = async (formId) => {
        if (!window.confirm('This will create a new form based on your denied request. You can then edit and resubmit it. Continue?')) {
            return;
        }

        try {
            setLoading(true);
            setLoadingMessage('Preparing form for resubmission...');

            const result = await Parse.Cloud.run('resubmitDeniedForm', { formId });

            if (result.success) {
                alert(`✅ ${result.message}\n\nYou can now edit and resubmit your form.`);
                // Navigate to the new form for editing
                navigate(`/home/form/${result.newFormId}`);
            } else {
                alert(`❌ ${result.message}`);
            }
        } catch (error) {
            console.error('Error resubmitting form:', error);
            alert('❌ Error preparing form for resubmission. Please try again later.');
        } finally {
            setLoading(false);
            setLoadingMessage('Generating PDF...');
        }
    };

    const cancelEventRequest = async (formId) => {
        const reason = window.prompt('Please provide a reason for cancelling this event request:');
        if (!reason || reason.trim() === '') {
            return;
        }

        if (!window.confirm('Are you sure you want to cancel this event request? This action cannot be undone.')) {
            return;
        }

        try {
            setLoading(true);
            setLoadingMessage('Cancelling event request...');

            const result = await Parse.Cloud.run('cancelEventRequest', {
                formId,
                reason: reason.trim()
            });

            if (result.success) {
                alert(`✅ ${result.message}`);
                // Refresh the forms list to show the updated status
                fetchForms();
            } else {
                alert(`❌ ${result.message}`);
            }
        } catch (error) {
            console.error('Error cancelling event request:', error);
            alert('❌ Error cancelling event request. Please try again later.');
        } finally {
            setLoading(false);
            setLoadingMessage('Generating PDF...');
        }
    };

    const renderActionButtons = (form) => {
        const buttons = [];
        const id = form.id;
        const isDraft = form.status === 'Draft';
        const isDenied = form.status === 'Denied';
        const isFullyApproved = form.status === 'Fully Approved';
        const isCancelled = form.status === 'Cancelled';
        const isSaveTheDate = form.formData && form.formData.isSaveTheDate;
        const isSubmitted = form.status === 'Submitted' || form.status === 'Partially Approved';

        if (isDraft) {
            buttons.push(
                // <button key="edit" onClick={() => editForm(id)} className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">Edit</button>,
                <button key="submit" className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600">Submit</button>
            );
        }

        // Add "Convert to Full Request" button for Save the Date entries
        if (isSaveTheDate) {
            buttons.push(
                <button
                    key="convert"
                    onClick={() => convertToFullRequest(id)}
                    className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                    Convert to Full Request
                </button>
            );
        }

        // Add "Resubmit" button for denied forms
        if (isDenied) {
            buttons.push(
                <button
                    key="resubmit"
                    onClick={() => resubmitDeniedForm(id)}
                    className="px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                    title="Create a new form based on this denied request for resubmission"
                >
                    🔄 Resubmit
                </button>
            );
        }

        // Add "Send Approval Emails" button for submitted forms that aren't denied or fully approved
        if (isSubmitted && !isDenied && !isFullyApproved) {
            buttons.push(
                <button
                    key="sendEmails"
                    onClick={() => sendApprovalEmails(id)}
                    className="px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-600"
                    title="Send approval request emails to approvers"
                >
                    📧 Send Emails
                </button>
            );
        }

        // Add "Cancel Request" button for non-cancelled, non-denied forms
        if (!isCancelled && !isDenied && !isDraft) {
            buttons.push(
                <button
                    key="cancel"
                    onClick={() => cancelEventRequest(id)}
                    className="px-2 py-1 bg-red-400 text-white rounded hover:bg-red-500"
                    title="Cancel this event request"
                >
                    🚫 Cancel Request
                </button>
            );
        }

        buttons.push(
            <button key="view" onClick={() => editForm(id)} className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">View</button>,
            <button key="duplicate" onClick={() => duplicateForm(id)} className="px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600">Duplicate</button>
        );

        // Only show delete for drafts or fully approved forms (to avoid accidental deletion of pending forms)
        if (isDraft || isFullyApproved) {
            buttons.push(
                <button key="delete" onClick={() => deleteForm(id)} className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600">Delete</button>
            );
        }

        buttons.push(
            <button key="pdf" onClick={() => generatePdf(id)} className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600">Generate PDF</button>
        );

        return buttons;
    };

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">My Requests</h1>
            {loading && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-5 rounded-lg shadow-xl flex items-center space-x-4">
                        <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-gray-700 font-medium">{loadingMessage}</span>
                    </div>
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                    <tr>
                        <th className="py-2 px-4 border-b">Event Name</th>
                        <th className="py-2 px-4 border-b">Created At</th>
                        <th className="py-2 px-4 border-b">Status</th>
                        <th className="py-2 px-4 border-b">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {forms.map((form, index) => (
                        <tr key={index} className="hover:bg-gray-100">
                            <td className="py-2 px-4 border-b">
                                {form.title}
                                {form.formData && form.formData.isSaveTheDate && 
                                    <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                                        Save the Date
                                    </span>
                                }
                            </td>
                            <td className="py-2 px-4 border-b">{formatDate(form.createdAt)}</td>
                            <td className="py-2 px-4 border-b">
                                <div className="flex flex-col">
                                    <span className={`px-2 py-1 rounded text-xs font-medium inline-block w-fit ${
                                        form.status === 'Draft' ? 'bg-gray-100 text-gray-800' :
                                        form.status === 'Cancelled' ? 'bg-gray-200 text-gray-700' :
                                        form.status === 'Denied' ? 'bg-red-100 text-red-800' :
                                        form.status === 'Fully Approved' ? 'bg-green-100 text-green-800' :
                                        form.status === 'Partially Approved' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-blue-100 text-blue-800'
                                    }`}>
                                        {form.status === 'Cancelled' ? '🚫 Cancelled' :
                                         form.status === 'Denied' ? '❌ Denied' :
                                         form.status === 'Fully Approved' ? '✅ Approved' :
                                         form.status === 'Partially Approved' ? '⏳ Partial' :
                                         form.status === 'Draft' ? '📝 Draft' :
                                         '📤 Submitted'}
                                    </span>
                                    {form.status === 'Cancelled' && form.cancellationReason && (
                                        <div className="mt-1 text-xs text-gray-600">
                                            <div className="font-medium">Cancelled on: {formatDate(form.cancelledAt)}</div>
                                            <div className="italic truncate max-w-xs" title={form.cancellationReason}>
                                                "{form.cancellationReason}"
                                            </div>
                                        </div>
                                    )}
                                    {form.status === 'Denied' && form.denialInfo && (
                                        <div className="mt-1 text-xs text-red-600">
                                            <div className="font-medium">Denied by: {form.denialInfo.role}</div>
                                            <div className="italic truncate max-w-xs" title={form.denialInfo.reason}>
                                                "{form.denialInfo.reason || 'No reason provided'}"
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </td>
                            <td className="py-2 px-4 border-b space-x-2">
                                {renderActionButtons(form)}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}